import { ArrowLeftOutlined, PhoneOutlined, ReloadOutlined } from '@ant-design/icons';
import { PERMISSIONS, type RequestStatus } from '@navicosa/shared-types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Timeline,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, errorText } from '../../api/client';
import {
  PERIOD_LABEL,
  QUOTE_STATUS_COLOR,
  QUOTE_STATUS_LABEL,
  type QuotationDetail,
  type QuotationListItem,
  phoneLocal,
  REQUEST_STATUS_COLOR,
  REQUEST_STATUS_LABEL,
  type RequestDetail,
  SOURCE_LABEL,
  type StaffUser,
  vnd,
} from '../../api/types';
import { useAuth } from '../../auth/AuthContext';

const NOTI_LABEL = {
  sent: 'Đã gửi',
  failed: 'Gửi lỗi',
  skipped: 'Không gửi',
  pending: 'Đang gửi',
} as const;
const NOTI_COLOR = { sent: 'green', failed: 'red', skipped: 'default', pending: 'blue' } as const;
const fmt = (d: string | null) => (d ? dayjs(d).format('DD/MM/YYYY') : '—');

export function RequestDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { message } = App.useApp();
  const { can } = useAuth();
  const canManage = can(PERMISSIONS.REQUESTS_MANAGE);
  const canAssign = can(PERMISSIONS.REQUESTS_ASSIGN);
  const [note, setNote] = useState('');
  const [transition, setTransition] = useState<RequestStatus | null>(null);
  const [transitionNote, setTransitionNote] = useState('');

  const q = useQuery({
    queryKey: ['request', id],
    queryFn: () => api.get<RequestDetail>(`/admin/rental-requests/${id}`),
  });
  const canQuote = can(PERMISSIONS.QUOTATIONS_MANAGE);
  const quotes = useQuery({
    queryKey: ['quotations', 'request', id],
    queryFn: async () =>
      (await api.page<QuotationListItem>('/admin/quotations', { request_id: id, limit: 50 })).items,
    enabled: can(PERMISSIONS.QUOTATIONS_READ),
  });
  const staff = useQuery({
    queryKey: ['staff-assignable'],
    queryFn: async () =>
      (await api.page<StaffUser>('/admin/users', { limit: 100, status: 'active' })).items,
    enabled: canAssign,
  });

  const save = async (fn: () => Promise<RequestDetail>, ok: string) => {
    try {
      qc.setQueryData(['request', id], await fn());
      void qc.invalidateQueries({ queryKey: ['requests'] });
      void qc.invalidateQueries({ queryKey: ['request-summary'] });
      message.success(ok);
      return true;
    } catch (e) {
      message.error(errorText(e));
      return false;
    }
  };

  if (q.isLoading) return <Spin />;
  if (!q.data) return <Empty description="Không tìm thấy yêu cầu hoặc bạn không được phân công" />;
  const r = q.data;
  const needsNote = transition === 'rejected' || transition === 'cancelled';
  const assignable = (staff.data ?? []).filter((u) =>
    u.roles.some((x) => ['sales', 'manager', 'admin', 'super_admin'].includes(x)),
  );

  return (
    <>
      <Space style={{ marginBottom: 16 }} wrap>
        <Button icon={<ArrowLeftOutlined />} onClick={() => nav(-1)}>
          Quay lại
        </Button>
        <Typography.Title level={4} style={{ margin: 0 }}>
          {r.request_code}
        </Typography.Title>
        <Tag color={REQUEST_STATUS_COLOR[r.status]}>{REQUEST_STATUS_LABEL[r.status]}</Tag>
        <Typography.Text type="secondary">
          {SOURCE_LABEL[r.source] ?? r.source} · {dayjs(r.created_at).format('DD/MM/YYYY HH:mm')}
        </Typography.Text>
      </Space>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card
            title="Thông tin yêu cầu"
            extra={
              <a href={`tel:${phoneLocal(r.contact_phone)}`}>
                <Button icon={<PhoneOutlined />}>Gọi {phoneLocal(r.contact_phone)}</Button>
              </a>
            }
          >
            <Descriptions
              column={{ xs: 1, sm: 1, md: 2, lg: 2, xl: 2, xxl: 2 }}
              size="small"
              bordered
            >
              <Descriptions.Item label="Người liên hệ">{r.contact_name}</Descriptions.Item>
              <Descriptions.Item label="Công ty">{r.contact_company ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Email">{r.contact_email ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="MST">{r.contact_tax_code ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Địa điểm" span={2}>
                {r.delivery_address}
                {r.location ? ` (${r.location.name})` : ''}
              </Descriptions.Item>
              <Descriptions.Item label="Giao xe">{fmt(r.delivery_at)}</Descriptions.Item>
              <Descriptions.Item label="Thời gian thuê">
                {fmt(r.rental_start_at)} → {fmt(r.rental_end_at)}
              </Descriptions.Item>
              <Descriptions.Item label="Thu hồi">{fmt(r.pickup_at)}</Descriptions.Item>
              <Descriptions.Item label="Hồ sơ khách">
                <Link to={`/customers/${r.customer.id}`}>{r.customer.full_name}</Link> ·{' '}
                {r.customer._count.rental_requests} yêu cầu
                {r.customer.phone_verified && (
                  <Tag color="green" style={{ marginLeft: 6 }}>
                    SĐT đã xác minh
                  </Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Ghi chú của khách" span={2}>
                {r.customer_note ?? '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Đồng ý xử lý dữ liệu" span={2}>
                {r.consent_at
                  ? `${dayjs(r.consent_at).format('DD/MM/YYYY HH:mm')} · chính sách ${r.consent_policy_version}`
                  : 'Nhân viên nhập – không có xác nhận trực tuyến'}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Card title="Thiết bị yêu cầu" style={{ marginTop: 16 }}>
            <Table
              size="small"
              rowKey="id"
              pagination={false}
              dataSource={r.items}
              columns={[
                {
                  title: 'Thiết bị',
                  render: (_, i) => (
                    <Space direction="vertical" size={0}>
                      <span>{i.product_snapshot.name}</span>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        {[
                          i.product_snapshot.product_code,
                          i.product_snapshot.brand,
                          i.product_snapshot.model,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </Typography.Text>
                    </Space>
                  ),
                },
                { title: 'SL', dataIndex: 'quantity', align: 'right' },
                { title: 'Kỳ', render: (_, i) => PERIOD_LABEL[i.rental_period] },
                {
                  title: 'Giá tham khảo lúc gửi',
                  render: (_, i) => i.product_snapshot.price?.display ?? <Tag>Liên hệ báo giá</Tag>,
                },
                {
                  title: 'Giá khách thấy',
                  render: (_, i) =>
                    i.client_expected_price !== null &&
                    i.client_expected_price !== i.expected_price ? (
                      <Typography.Text type="warning">
                        {vnd(i.client_expected_price)} (khác)
                      </Typography.Text>
                    ) : (
                      '—'
                    ),
                },
                { title: 'Ghi chú', dataIndex: 'customer_note' },
              ]}
            />
            <Typography.Paragraph
              type="secondary"
              style={{ marginTop: 8, marginBottom: 0, fontSize: 12 }}
            >
              Giá là giá tham khảo lưu tại thời điểm khách gửi, chưa phải báo giá. Cần xác minh xe,
              lịch và địa điểm trước khi chốt (PRD 2.5).
            </Typography.Paragraph>
          </Card>

          {can(PERMISSIONS.QUOTATIONS_READ) && (
            <Card
              title="Báo giá"
              style={{ marginTop: 16 }}
              extra={
                canQuote &&
                !['cancelled', 'rejected', 'converted', 'accepted'].includes(r.status) &&
                !(quotes.data ?? []).some((x) => x.status === 'draft') && (
                  <Button
                    type="primary"
                    onClick={async () => {
                      try {
                        const created = await api.post<QuotationDetail>(
                          `/admin/rental-requests/${id}/quotations`,
                        );
                        void qc.invalidateQueries({ queryKey: ['request', id] });
                        nav(`/quotations/${created.id}`);
                      } catch (e) {
                        message.error(errorText(e));
                      }
                    }}
                  >
                    {(quotes.data ?? []).length ? 'Lập báo giá mới' : 'Lập báo giá'}
                  </Button>
                )
              }
            >
              {(quotes.data ?? []).length === 0 ? (
                <Typography.Text type="secondary">
                  Chưa có báo giá. Hệ thống tự gợi ý đơn giá và cách tính rẻ nhất từ bảng giá; nhân
                  viên chỉnh lại trước khi gửi.
                </Typography.Text>
              ) : (
                <Table
                  size="small"
                  rowKey="id"
                  pagination={false}
                  dataSource={quotes.data}
                  onRow={(x) => ({
                    onClick: () => nav(`/quotations/${x.id}`),
                    style: { cursor: 'pointer' },
                  })}
                  columns={[
                    {
                      title: 'Mã',
                      render: (_, x) => <Link to={`/quotations/${x.id}`}>{x.quote_code}</Link>,
                    },
                    { title: 'Tổng cộng', align: 'right', render: (_, x) => vnd(x.total_amount) },
                    {
                      title: 'Hiệu lực',
                      render: (_, x) =>
                        x.valid_until ? dayjs(x.valid_until).format('DD/MM/YYYY') : '—',
                    },
                    {
                      title: 'Trạng thái',
                      render: (_, x) => (
                        <Space size={4}>
                          <Tag color={QUOTE_STATUS_COLOR[x.status]}>
                            {QUOTE_STATUS_LABEL[x.status]}
                          </Tag>
                          {x.booking && <Tag color="green">{x.booking.booking_code}</Tag>}
                        </Space>
                      ),
                    },
                  ]}
                />
              )}
            </Card>
          )}

          <Card title="Ghi chú nội bộ" style={{ marginTop: 16 }}>
            {r.notes.length === 0 ? (
              <Typography.Text type="secondary">Chưa có ghi chú</Typography.Text>
            ) : (
              <Timeline
                items={r.notes.map((n) => ({
                  children: (
                    <>
                      <Typography.Text strong>{n.user.full_name}</Typography.Text>{' '}
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        {dayjs(n.created_at).format('DD/MM HH:mm')}
                      </Typography.Text>
                      <div style={{ whiteSpace: 'pre-wrap' }}>{n.note}</div>
                    </>
                  ),
                }))}
              />
            )}
            {canManage && (
              <Space.Compact style={{ width: '100%', marginTop: 8 }}>
                <Input.TextArea
                  rows={2}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Đã gọi khách, khảo sát mặt bằng, nguồn xe…"
                />
                <Button
                  type="primary"
                  disabled={!note.trim()}
                  onClick={async () => {
                    if (
                      await save(
                        () => api.post(`/admin/rental-requests/${id}/notes`, { note: note.trim() }),
                        'Đã thêm ghi chú',
                      )
                    )
                      setNote('');
                  }}
                >
                  Thêm
                </Button>
              </Space.Compact>
            )}
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title="Xử lý">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Typography.Text type="secondary">Phụ trách</Typography.Text>
              {canAssign ? (
                <Select
                  style={{ width: '100%' }}
                  allowClear
                  placeholder="Chưa phân công"
                  value={r.assigned_user?.id}
                  options={assignable.map((u) => ({ value: u.id, label: u.full_name }))}
                  onChange={(v) =>
                    void save(
                      () =>
                        api.patch(`/admin/rental-requests/${id}/assign`, {
                          assigned_user_id: v ?? null,
                        }),
                      'Đã phân công',
                    )
                  }
                />
              ) : (
                <Typography.Text strong>
                  {r.assigned_user?.full_name ?? 'Chưa phân công'}
                </Typography.Text>
              )}
              <Typography.Text type="secondary" style={{ marginTop: 8 }}>
                Chuyển trạng thái
              </Typography.Text>
              {r.allowed_transitions.length === 0 ? (
                <Typography.Text type="secondary">Không có thao tác</Typography.Text>
              ) : (
                <Space wrap>
                  {canManage &&
                    r.allowed_transitions.map((s) => (
                      <Button
                        key={s}
                        danger={s === 'rejected' || s === 'cancelled'}
                        onClick={() => (setTransition(s), setTransitionNote(''))}
                      >
                        {REQUEST_STATUS_LABEL[s]}
                      </Button>
                    ))}
                </Space>
              )}
              <Alert
                type="info"
                showIcon
                style={{ marginTop: 8 }}
                message="Trạng thái 'Đã gửi báo giá', 'Khách chấp nhận', 'Chuyển đơn' tự cập nhật theo báo giá – không chọn tay."
              />
            </Space>
          </Card>

          <Card title="Lịch sử trạng thái" style={{ marginTop: 16 }}>
            <Timeline
              items={r.status_history.map((h) => ({
                color:
                  REQUEST_STATUS_COLOR[h.to_status] === 'default'
                    ? 'gray'
                    : REQUEST_STATUS_COLOR[h.to_status],
                children: (
                  <>
                    <Typography.Text strong>{REQUEST_STATUS_LABEL[h.to_status]}</Typography.Text>
                    <div style={{ fontSize: 12, color: '#737B86' }}>
                      {dayjs(h.created_at).format('DD/MM/YYYY HH:mm')} ·{' '}
                      {h.user?.full_name ?? 'Khách'}
                    </div>
                    {h.note && <div>{h.note}</div>}
                  </>
                ),
              }))}
            />
          </Card>

          <Card title="Thông báo Zalo OA" style={{ marginTop: 16 }}>
            {r.notifications.length === 0 ? (
              <Typography.Text type="secondary">
                Không có thông báo (nhân viên nhập hoặc chưa ai bật nhận thông báo)
              </Typography.Text>
            ) : (
              <Space direction="vertical" style={{ width: '100%' }}>
                {r.notifications.map((n) => (
                  <Space key={n.id} style={{ justifyContent: 'space-between', width: '100%' }} wrap>
                    <span>{n.recipient?.full_name ?? '—'}</span>
                    <Space size={4}>
                      <Tag color={NOTI_COLOR[n.status]} title={n.error_message ?? undefined}>
                        {NOTI_LABEL[n.status]}
                      </Tag>
                      {n.status === 'failed' && canManage && (
                        <Button
                          size="small"
                          icon={<ReloadOutlined />}
                          onClick={async () => {
                            try {
                              await api.post(`/admin/notifications/${n.id}/retry`);
                              void q.refetch();
                            } catch (e) {
                              message.error(errorText(e));
                            }
                          }}
                        >
                          Gửi lại
                        </Button>
                      )}
                    </Space>
                    {n.error_message && (
                      <Typography.Text type="secondary" style={{ fontSize: 12, width: '100%' }}>
                        {n.error_message}
                      </Typography.Text>
                    )}
                  </Space>
                ))}
              </Space>
            )}
          </Card>
        </Col>
      </Row>

      <Modal
        open={transition !== null}
        title={transition ? `Chuyển sang "${REQUEST_STATUS_LABEL[transition]}"` : ''}
        okText="Xác nhận"
        okButtonProps={{
          disabled: needsNote && transitionNote.trim().length < 3,
          danger: needsNote,
        }}
        onCancel={() => setTransition(null)}
        onOk={async () => {
          if (!transition) return;
          if (
            await save(
              () =>
                api.patch(`/admin/rental-requests/${id}/status`, {
                  status: transition,
                  note: transitionNote.trim() || null,
                }),
              'Đã cập nhật trạng thái',
            )
          ) {
            setTransition(null);
          }
        }}
      >
        <Input.TextArea
          rows={3}
          value={transitionNote}
          onChange={(e) => setTransitionNote(e.target.value)}
          placeholder={needsNote ? 'Bắt buộc nhập lý do' : 'Ghi chú (không bắt buộc)'}
        />
      </Modal>
    </>
  );
}
